import React, { useEffect } from 'react';
import { TextField, Box, Typography, Alert } from '@mui/material';

interface Props {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

const ProblemContext: React.FC<Props> = ({ data, onChange, onComplete }) => {
  const handleChange = (field: string, value: any) => {
    const newData = { ...data, [field]: value };
    onChange(newData);

    const isComplete = !!(
      newData.problemStatement?.length >= 50 &&
      newData.currentSolutions?.length >= 50
    );
    onComplete(isComplete);
  };

  useEffect(() => {
    const isComplete = !!(
      data.problemStatement?.length >= 50 &&
      data.currentSolutions?.length >= 50
    );
    onComplete(isComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.problemStatement, data.currentSolutions]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Problem & Context</Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Describe the problem your invention addresses and the current situation. Do not describe the invention here, instead focus on the problem found with existing technology, processes or services.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Guidelines for Patent-Ready Description:
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Problem Statement:</strong> Clearly articulate the technical problem or challenge. Be specific about what doesn't work well, what's inefficient, or what gap exists. Avoid vague statements like "current solutions are bad" – explain <em>why</em> and <em>how</em> they fall short. Focus on technical problems, not just business or market problems.
        </Typography>

        <Typography variant="body2">
          <strong>Current Solutions & Limitations:</strong> List existing approaches (patents, products, methods) and objectively identify their specific limitations. Use factual language: "Method X requires manual calibration, taking 2 hours" rather than "Method X is terrible." This section establishes the technical context and demonstrates your understanding of the field, which strengthens your patent claims by showing what your invention improves upon.
        </Typography>
      </Alert>

      <TextField
        fullWidth
        required
        multiline
        rows={4}
        label="Problem Statement"
        value={data.problemStatement || ''}
        onChange={(e) => handleChange('problemStatement', e.target.value)}
        margin="normal"
        error={data.problemStatement && data.problemStatement.length < 50}
        helperText={
          data.problemStatement && data.problemStatement.length < 50
            ? `Must be at least 50 characters (currently ${data.problemStatement.length})`
            : "Clearly describe the problem your invention solves (min. 50 characters)"
        }
        inputProps={{ maxLength: 1000 }}
      />

      <TextField
        fullWidth
        required
        multiline
        rows={5}
        label="Current Solutions & Their Limitations"
        value={data.currentSolutions || ''}
        onChange={(e) => handleChange('currentSolutions', e.target.value)}
        margin="normal"
        error={data.currentSolutions && data.currentSolutions.length < 50}
        helperText={
          data.currentSolutions && data.currentSolutions.length < 50
            ? `Must be at least 50 characters (currently ${data.currentSolutions.length})`
            : "Briefly describe existing methods, systems, or devices. Identify their limitations or problems — the pain points your invention addresses. Keep it factual; don't disparage prior art. (min. 50 characters)"
        }
        inputProps={{ maxLength: 1500 }}
      />
    </Box>
  );
};

export default ProblemContext;
